// Shared PDF/Excel export helpers. Every report page builds an
// `exportSpec` and hands it to ReportShell — the shell then renders
// Download PDF / Download Excel buttons that fan out to these helpers.
//
// PDF layout is deliberately opinionated so every report shares the
// same brand chrome (logo top-left, title + meta top-right, table
// below). Excel is a plain workbook with one row of column headers so
// accountants can pivot/sum without post-processing.
//
// Numeric-column contract (Iter-318): a column may declare
//   { numeric: true, format: (n) => "Rp 100,000", numFmt: "#,##0" }
// The PDF path uses `format(n)` so the on-screen currency label
// (Rp / BHD / EUR / …) is preserved. The Excel path writes the RAW
// number as `{ t: "n", v: n, z: numFmt }` so accountants can drop
// `=SUM(A1:A10)` on any financial column without post-processing.
// Row values for numeric columns MUST be raw numbers (or null / "" for
// blank). Text columns (Date, Order #, Guest Name, Care Team) are
// untouched — they still ride as plain strings.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const DEFAULT_MONEY_FMT = "#,##0";

// Cache the logo → data-URL so repeated exports don't re-decode. The
// promise resolves once and is reused for every subsequent PDF.
let _logoPromise = null;
function _loadLogoDataUrl() {
  if (_logoPromise) return _logoPromise;
  _logoPromise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch {
        resolve(null);  // cross-origin taint — degrade gracefully to text-only header
      }
    };
    img.onerror = () => resolve(null);
    img.src = "/brand/cocoon-logo.png";
  });
  return _logoPromise;
}

function _sanitiseFileName(name) {
  return String(name || "report").replace(/[^\w\-]+/g, "_").replace(/^_+|_+$/g, "").toLowerCase();
}

function _formatDateRange(range) {
  if (!range) return "All time";
  const { date_from, date_to } = range;
  if (!date_from && !date_to) return "All time";
  if (date_from === date_to) return date_from || "—";
  return `${date_from || "…"} → ${date_to || "…"}`;
}

// PDF cell renderer — numeric values go through `col.format` (branch
// currency label preserved) while text values are cast to string.
function _pdfCell(col, v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") {
    if (typeof col.format === "function") return col.format(v);
    return v.toLocaleString("en-GB");
  }
  return String(v);
}

// Excel cell renderer — numeric columns emit a typed number cell with
// the branch's number format so `=SUM(...)` works with zero post-
// processing. Text columns emit plain strings verbatim.
function _xlsxCell(col, v) {
  if (col && col.numeric) {
    if (v === null || v === undefined || v === "") return "";
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n)) return "";
    return { t: "n", v: n, z: col.numFmt || DEFAULT_MONEY_FMT };
  }
  if (v === null || v === undefined) return "";
  // Footer rows sometimes pass a raw number under a non-numeric column
  // (e.g. "Totals · 3 orders" in the first cell). Keep passthrough.
  return v;
}

/**
 * Download the given rows as a formatted PDF.
 * @param {Object}   spec
 * @param {String}   spec.title
 * @param {String=}  spec.branchName
 * @param {Object=}  spec.range    { date_from, date_to } or null for "All time"
 * @param {Array}    spec.columns  [{ header, key, align?, numeric?, format?, numFmt? }]
 * @param {Array}    spec.rows     Array of plain objects keyed by column.key
 * @param {String=}  spec.filename
 * @param {String=}  spec.subtitle
 * @param {Array=}   spec.footerRow  Optional array of cell values aligned with columns
 *                                    (numbers → formatted; strings → passthrough).
 */
export async function downloadReportPdf(spec) {
  const {
    title, branchName, range, columns, rows,
    filename, subtitle, footerRow,
  } = spec;

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 32;

  // --- Header chrome ------------------------------------------------------
  const logo = await _loadLogoDataUrl();
  const headerTop = margin;
  const headerH = 46;
  if (logo && logo.dataUrl) {
    const ratio = logo.w / logo.h;
    const h = headerH;
    const w = h * ratio;
    try { doc.addImage(logo.dataUrl, "PNG", margin, headerTop, w, h); } catch { /* image tainted → skip */ }
  }
  // Wordmark to the right of the logo — ensures brand shows even if the
  // logo image ever fails to load in a customer's browser.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(20, 20, 20);
  doc.text("Cocoon Nest", margin + (logo ? 56 : 0), headerTop + 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text("Cocoon Medical Spa · Location Reports", margin + (logo ? 56 : 0), headerTop + 34);

  // Right side: report title + branch + range
  const rightX = pageW - margin;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(20, 20, 20);
  doc.text(title, rightX, headerTop + 12, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  const metaLines = [
    branchName ? `Location: ${branchName}` : null,
    `Date range: ${_formatDateRange(range)}`,
    subtitle || null,
    `Generated: ${new Date().toLocaleString("en-GB", { timeZone: "Asia/Makassar" })}`,
  ].filter(Boolean);
  metaLines.forEach((line, i) => {
    doc.text(line, rightX, headerTop + 28 + i * 12, { align: "right" });
  });

  // Divider under the header block
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  const dividerY = headerTop + headerH + 12;
  doc.line(margin, dividerY, pageW - margin, dividerY);

  // --- Body table ---------------------------------------------------------
  const head = [columns.map((c) => c.header)];
  const body = rows.map((r) => columns.map((c) => _pdfCell(c, r[c.key])));

  // Footer — cells may be numbers (formatted via column) or strings.
  const foot = footerRow ? [footerRow.map((cell, i) => {
    const col = columns[i];
    if (typeof cell === "number") return col ? _pdfCell(col, cell) : String(cell);
    return cell === null || cell === undefined ? "" : String(cell);
  })] : undefined;

  autoTable(doc, {
    startY: dividerY + 12,
    head,
    body,
    foot,
    styles: { fontSize: 8.5, cellPadding: 5, overflow: "linebreak" },
    headStyles: { fillColor: [40, 40, 40], textColor: [255, 255, 255], fontStyle: "bold" },
    footStyles: { fillColor: [235, 235, 235], textColor: [20, 20, 20], fontStyle: "bold" },
    columnStyles: columns.reduce((acc, c, i) => {
      if (c.align === "right") acc[i] = { halign: "right" };
      return acc;
    }, {}),
    theme: "grid",
    margin: { left: margin, right: margin },
    didDrawPage: () => {
      // Page number footer.
      const pageCount = doc.getNumberOfPages();
      const currentPage = doc.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${currentPage} of ${pageCount}`,
        pageW - margin,
        doc.internal.pageSize.getHeight() - 16,
        { align: "right" },
      );
    },
  });

  // Handle empty rows — autoTable draws head only; add a friendly note.
  if (rows.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text("No data available for this date range.", pageW / 2, dividerY + 60, { align: "center" });
  }

  const fname = `${_sanitiseFileName(filename || title)}.pdf`;
  doc.save(fname);
}

/**
 * Download the same data as an .xlsx workbook. One sheet named after
 * the report; a first metadata row so accountants know which range /
 * branch they're looking at even after the file leaves the app.
 *
 * Numeric columns (columns with `numeric: true`) are written as typed
 * number cells with the column's `numFmt` (default `#,##0`) so
 * `=SUM(D2:D42)` works instantly on Original Price, Adjustment, Subtotal,
 * Service Charge, Tax and Final Total.
 */
export function downloadReportXlsx(spec) {
  const { title, branchName, range, columns, rows, filename, footerRow } = spec;

  const meta = [
    ["Cocoon Nest · Location Reports"],
    [title],
    branchName ? ["Location", branchName] : null,
    ["Date range", _formatDateRange(range)],
    ["Generated", new Date().toLocaleString("en-GB", { timeZone: "Asia/Makassar" })],
    [],
  ].filter(Boolean);

  const header = columns.map((c) => c.header);
  const body = rows.map((r) => columns.map((c) => _xlsxCell(c, r[c.key])));

  const aoa = [...meta, header, ...body];
  if (footerRow) {
    aoa.push(footerRow.map((cell, i) => {
      const col = columns[i];
      // Numeric column → typed number cell; text column → passthrough.
      if (col && col.numeric && typeof cell === "number") {
        return { t: "n", v: cell, z: col.numFmt || DEFAULT_MONEY_FMT };
      }
      if (cell === null || cell === undefined) return "";
      // Footers frequently mix labels ("Totals · 3 orders") into text
      // columns — those must stay strings so they render verbatim.
      return typeof cell === "number" ? cell : cell;
    }));
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Auto-size columns to the widest cell so accountants don't fiddle.
  // Width heuristic uses the display-length: numeric columns fall back
  // to their unformatted digit count so a big Rupiah total still fits.
  ws["!cols"] = columns.map((c, i) => {
    const widths = [String(c.header || "").length];
    body.forEach((r) => {
      const cell = r[i];
      if (cell && typeof cell === "object" && "v" in cell) {
        widths.push(String(cell.v ?? "").length + 2);
      } else {
        widths.push(String(cell ?? "").length);
      }
    });
    widths.push(12);
    return { wch: Math.min(Math.max(...widths) + 2, 48) };
  });

  const wb = XLSX.utils.book_new();
  const sheetName = String(title || "Report").slice(0, 30).replace(/[\\/*?:[\]]/g, " ");
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const fname = `${_sanitiseFileName(filename || title)}.xlsx`;
  XLSX.writeFile(wb, fname);
}
